import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Drawer, Dropdown, Menu, Select } from "antd";
import type { MenuProps } from "antd";
import { DownOutlined, AlignRightOutlined } from "@ant-design/icons";
import logo from "../assets/logo.svg";
import honesti from "../assets/honesti.svg";
import { solutionNavItems } from "../data/navigation";
import { NavbarSearch } from "./NavbarSearch";

type DrawerAccordion = "solutions" | "software" | null;

const languageSelectOptions = (
    <Select.Option value="en">en</Select.Option>
);

export const Header = () => {
    const navigate = useNavigate();
    const [openDrawer, setOpenDrawer] = useState(false);
    const [openAccordion, setOpenAccordion] = useState<DrawerAccordion>(null);
    const [searchExpanded, setSearchExpanded] = useState(false);
    const mobileSearchGrowRef = useRef<HTMLDivElement>(null);

    const softwareNavItemsOrdered = [
        { label: "EasyOn", path: "/software/easyon" },
        { label: "HonestiIQ", path: "/software/honesti-crm" },
        { label: "WhatsApp Bulk Sender", path: "/software/whatsapp-bulk-sender" },
        { label: "Social Media Automation", path: "/software/socialmedia-automation-software" },
        { label: "WhatsApp API Platform", path: "/software/whatsapp-api-platform" },
        { label: "SMS, Call & Email Broadcast", path: "/software/sms-call-email-broadcast" },
    ];

    const solutionItems: MenuProps["items"] = solutionNavItems.map((item, index) => ({
        key: index,
        label: (
            <Link to={item.path} onClick={() => setOpenDrawer(false)}>
                {item.label}
            </Link>
        ),
    }));

    const softwareMenuItems: MenuProps["items"] = softwareNavItemsOrdered.map((item, index) => ({
        key: index,
        label: (
            <Link to={item.path} onClick={() => setOpenDrawer(false)}>
                {item.label}
            </Link>
        ),
    }));

    const solutionMenu = <Menu items={solutionItems} />;
    const softwareMenu = <Menu items={softwareMenuItems} />;

    const closeDrawer = () => {
        setOpenDrawer(false);
        setOpenAccordion(null);
    };

    const toggleAccordion = (section: Exclude<DrawerAccordion, null>) => {
        setOpenAccordion((current) => (current === section ? null : section));
    };

    const navigateFromDrawer = (path: string) => {
        closeDrawer();
        navigate(path);
    };

    return (
        <>
            <div className={`header${searchExpanded ? " header--search-open" : ""}`}>
                <div className="first-part">
                    <Link to="/" className="header-logo-link">
                        <img alt="logo" className="logo" src={logo} />
                        <img alt="title" className="logo-title" src={honesti} />
                    </Link>
                    <div className="header-nav-desktop">
                        <div className="knowUs" onClick={() => navigate("/")}>
                            Home
                        </div>
                        <div className="knowUs" onClick={() => navigate("/know-us")}>
                            Know Us
                        </div>
                        <Dropdown className="getSolutions" overlay={softwareMenu} placement="bottom" arrow>
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <span style={{ marginRight: "10px" }}>Use Softwares</span>
                                <DownOutlined />
                            </div>
                        </Dropdown>
                        <Dropdown className="getSolutions" overlay={solutionMenu} placement="bottom" arrow>
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <span style={{ marginRight: "10px" }}>Get Solutions</span>
                                <DownOutlined />
                            </div>
                        </Dropdown>
                    </div>
                </div>
                <div className="header-search-grow" ref={mobileSearchGrowRef} aria-hidden={!searchExpanded} />
                <div className="second-part header-toolbar header-end">
                    <NavbarSearch
                        onExpandedChange={setSearchExpanded}
                        mobileGrowRef={mobileSearchGrowRef}
                    />
                    <Select
                        title="Select Language"
                        className="lang header-lang"
                        popupClassName="header-lang-dropdown"
                        style={{ borderRadius: "16px" }}
                        defaultValue="en"
                    >
                        {languageSelectOptions}
                    </Select>
                    <Button className="contact header-contact-desktop" onClick={() => navigate("/contact")}>
                        Contact
                    </Button>
                    <button
                        type="button"
                        className="header-nav-mobile"
                        aria-label="Open menu"
                        onClick={() => setOpenDrawer(true)}
                    >
                        <AlignRightOutlined />
                    </button>
                </div>
            </div>
            <Drawer
                title={
                    <div>
                        <img alt="logo" style={{ width: "50px" }} src={logo} />
                        <img alt="menu" style={{ height: "50px" }} src={honesti} />
                    </div>
                }
                onClose={closeDrawer}
                open={openDrawer}
                className="header-drawer"
            >
                <div className="header-drawer__nav">
                    <button type="button" className="header-drawer__link" onClick={() => navigateFromDrawer("/")}>
                        Home
                    </button>
                    <button type="button" className="header-drawer__link" onClick={() => navigateFromDrawer("/know-us")}>
                        Know Us
                    </button>

                    <div className="header-drawer__accordion">
                        <button
                            type="button"
                            className="header-drawer__link header-drawer__accordion-trigger"
                            aria-expanded={openAccordion === "software"}
                            onClick={() => toggleAccordion("software")}
                        >
                            <span>Use Softwares</span>
                            <DownOutlined
                                className={`header-drawer__chevron${openAccordion === "software" ? " is-open" : ""}`}
                            />
                        </button>
                        <div
                            className={`header-drawer__accordion-panel${openAccordion === "software" ? " is-open" : ""}`}
                        >
                            <div className="header-drawer__accordion-panel-inner">
                                {softwareNavItemsOrdered.map((item) => (
                                    <button
                                        key={item.path}
                                        type="button"
                                        className="header-drawer__subitem"
                                        onClick={() => navigateFromDrawer(item.path)}
                                    >
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="header-drawer__accordion">
                        <button
                            type="button"
                            className="header-drawer__link header-drawer__accordion-trigger"
                            aria-expanded={openAccordion === "solutions"}
                            onClick={() => toggleAccordion("solutions")}
                        >
                            <span>Get Solutions</span>
                            <DownOutlined
                                className={`header-drawer__chevron${openAccordion === "solutions" ? " is-open" : ""}`}
                            />
                        </button>
                        <div
                            className={`header-drawer__accordion-panel${openAccordion === "solutions" ? " is-open" : ""}`}
                        >
                            <div className="header-drawer__accordion-panel-inner">
                                {solutionNavItems.map((item) => (
                                    <button
                                        key={item.path}
                                        type="button"
                                        className="header-drawer__subitem"
                                        onClick={() => navigateFromDrawer(item.path)}
                                    >
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <button type="button" className="header-drawer__link" onClick={() => navigateFromDrawer("/contact")}>
                        Contact
                    </button>
                </div>
            </Drawer>
        </>
    );
};
